import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { Injectable, inject, signal, computed, effect } from '@angular/core';

import { StatusSelectDialogComponent } from '@cabinet/components/status-select-dialog/status-select-dialog.component';
import { StatusModel } from '@core/models/status.model';
import { TaskModel } from '@core/models/task.model';
import * as fromRoot from '@core/redux';
import { LoadProjectStatusesAction } from '@core/redux/status/status.actions';
import * as TaskActions from '@core/redux/task/task.actions';
import { Store } from '@ngrx/store';

interface TaskTransferState {
  showStatusDialog: boolean;
  currentTask: TaskModel | null;
  targetProjectId: string | null;
}

interface PendingTransfer {
  task: TaskModel;
  targetProjectId: string;
}

@Injectable({
  providedIn: 'root',
})
export class TaskTransferService {
  private readonly store = inject<Store<fromRoot.State>>(Store);
  private readonly dialog = inject(Dialog);

  private readonly state = signal<TaskTransferState>({
    showStatusDialog: false,
    currentTask: null,
    targetProjectId: null,
  });

  // Сигнал для отслеживания текущего переноса
  private readonly pendingTransfer = signal<PendingTransfer | null>(null);

  // Храним ссылку на текущий открытый диалог
  private currentDialog: DialogRef | null = null;

  // Публичные сигналы для чтения состояния
  readonly showStatusDialog = computed(() => this.state().showStatusDialog);
  readonly currentTask = computed(() => this.state().currentTask);
  readonly targetProjectId = computed(() => this.state().targetProjectId);

  // Сигнал для статусов из store
  private readonly allStatuses = this.store.selectSignal(fromRoot.getStatusEntities);
  private readonly projects = this.store.selectSignal(fromRoot.getProjectEntities);

  constructor() {
    // Эффект для обработки переноса задачи
    effect(
      () => {
        const transfer = this.pendingTransfer();
        const statuses = this.allStatuses();
        const projects = this.projects();

        // Если нет активного переноса, выходим
        if (!transfer) {
          return;
        }

        // Проверяем, загружены ли статусы обоих проектов
        if (!this.areStatusesLoaded(statuses, transfer.task.projectId, transfer.targetProjectId)) {
          return;
        }

        // Ищем соответствующий статус
        const matchingStatus = this.findMatchingStatus(statuses, transfer.task, transfer.targetProjectId);

        if (matchingStatus) {
          // Если нашли подходящий статус, сразу перемещаем задачу
          this.moveTaskToProject(transfer.task.id, transfer.targetProjectId, matchingStatus.id);
          // Очищаем pending transfer
          this.pendingTransfer.set(null);
        } else {
          // Закрываем предыдущий диалог, если он открыт
          if (this.currentDialog) {
            this.currentDialog.close();
          }

          // Если не нашли, показываем диалог выбора статуса
          const targetProject = projects.find(p => p.id === transfer.targetProjectId);
          const targetStatuses = statuses.filter(s => s.projectId === transfer.targetProjectId);

          this.currentDialog = this.dialog.open(StatusSelectDialogComponent, {
            data: {
              statuses: targetStatuses,
              projectName: targetProject?.name || 'Новый проект',
            },
          });

          this.currentDialog.closed.subscribe((result: StatusModel | undefined) => {
            if (result) {
              this.moveTaskToProject(transfer.task.id, transfer.targetProjectId, result.id);
            }
            this.pendingTransfer.set(null);
            this.currentDialog = null;
          });
        }
      },
      { allowSignalWrites: true }
    );
  }

  private setState(newState: Partial<TaskTransferState>): void {
    this.state.update(current => ({ ...current, ...newState }));
  }

  /**
   * Перемещает задачу в другой проект
   */
  private moveTaskToProject(taskId: string, projectId: string, statusId: string): void {
    this.store.dispatch(
      new TaskActions.MoveToProjectAction({
        taskId: taskId,
        projectId: projectId,
        statusId: statusId,
      })
    );

    this.setState({
      showStatusDialog: false,
      currentTask: null,
      targetProjectId: null,
    });
  }

  /**
   * Проверяет, загружены ли статусы для обоих проектов
   */
  private areStatusesLoaded(statuses: StatusModel[], sourceProjectId: string, targetProjectId: string): boolean {
    const sourceProjectStatuses = statuses.filter(s => s.projectId === sourceProjectId);
    const targetProjectStatuses = statuses.filter(s => s.projectId === targetProjectId);
    return sourceProjectStatuses.length > 0 && targetProjectStatuses.length > 0;
  }

  /**
   * Находит соответствующий статус в целевом проекте
   */
  private findMatchingStatus(statuses: StatusModel[], task: TaskModel, targetProjectId: string): StatusModel | null {
    const sourceProjectStatuses = statuses.filter(s => s.projectId === task.projectId);
    const currentStatus = sourceProjectStatuses.find(s => s.id === task.statusId);

    if (!currentStatus) {
      return null;
    }

    const targetProjectStatuses = statuses.filter(s => s.projectId === targetProjectId);
    return targetProjectStatuses.find(s => s.name.toLowerCase() === currentStatus.name.toLowerCase()) || null;
  }

  /**
   * Обрабатывает перенос задачи в другой проект
   */
  handleTaskTransfer(task: TaskModel, targetProjectId: string): void {
    // Проверяем, не пытаемся ли мы переместить задачу в тот же проект
    if (task.projectId === targetProjectId) {
      return;
    }

    // Сначала загружаем статусы целевого проекта
    this.store.dispatch(new LoadProjectStatusesAction(targetProjectId));

    // Устанавливаем новый перенос
    this.pendingTransfer.set({ task, targetProjectId });
  }
}
